import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import VideoDialog from "@/components/VideoDialog";
import {
  cues,
  playBeep,
  announceNextExercise,
  announceRestEnd,
  announceSessionStart,
  announceSessionComplete,
} from "@/lib/audio";
import { toast } from "sonner";
import { Check, Pause, Play, SkipForward, X, ArrowLeft, Volume2, VolumeX, PlayCircle } from "lucide-react";

export default function Session() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [w, setW] = useState(null);
  const [library, setLibrary] = useState([]);
  const [videoEx, setVideoEx] = useState(null);
  const [progressions, setProgressions] = useState({}); // exercise_id -> {last, suggested}
  const [weights, setWeights] = useState({}); // exercise_id -> chosen kg (string)
  const [exIdx, setExIdx] = useState(0);
  const startAnnouncedRef = useRef(false);
  const [setIdx, setSetIdx] = useState(0);
  const [resting, setResting] = useState(false);
  const [restLeft, setRestLeft] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [completedSets, setCompletedSets] = useState({}); // { exIdx: [count] }
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("fc_sound") !== "off");
  const startRef = useRef(Date.now());
  const restTimerRef = useRef(null);

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      localStorage.setItem("fc_sound", next ? "on" : "off");
      if (next) playBeep({ freq: 880, duration: 0.15, volume: 0.3 }); // preview
      return next;
    });
  };

  // Announce session start once the workout has loaded (needs user click already made on Start btn)
  useEffect(() => {
    if (!w || !soundOn || startAnnouncedRef.current) return;
    startAnnouncedRef.current = true;
    if (w.exercises?.[0]?.name) announceSessionStart(w.exercises[0].name);
  }, [w, soundOn]);

  useEffect(() => {
    api.get(`/workouts/${id}`).then((r) => {
      setW(r.data);
      // fetch progression per exercise (parallel)
      const uniq = Array.from(new Set(r.data.exercises.map((e) => e.exercise_id)));
      Promise.all(
        uniq.map((exId) =>
          api.get(`/exercises/${exId}/progression`).then((rr) => [exId, rr.data]).catch(() => null)
        )
      ).then((results) => {
        const map = {};
        const w0 = {};
        for (const item of results) {
          if (!item) continue;
          const [exId, data] = item;
          map[exId] = data;
          if (data.suggested?.weight_kg != null) w0[exId] = String(data.suggested.weight_kg);
        }
        setProgressions(map);
        setWeights(w0);
      });
    });
    api.get("/exercises").then((r) => setLibrary(r.data));
  }, [id]);

  const libById = useMemo(() => {
    const m = {};
    for (const e of library) m[e.id] = e;
    return m;
  }, [library]);
  const enrichEx = (ex) => (ex ? { ...(libById[ex.exercise_id] || {}), ...ex } : null);

  // main elapsed timer
  useEffect(() => {
    const t = setInterval(() => {
      if (!paused) setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 500);
    return () => clearInterval(t);
  }, [paused]);

  // rest timer
  useEffect(() => {
    if (!resting) return;
    restTimerRef.current = setInterval(() => {
      setRestLeft((s) => {
        if (s <= 1) {
          clearInterval(restTimerRef.current);
          setResting(false);
          if (soundOn) announceRestEnd();
          return 0;
        }
        // countdown tick on last 3 seconds (before the "end" beep)
        if (soundOn && s <= 4 && s > 1) cues.countdownTick();
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(restTimerRef.current);
  }, [resting, soundOn]);

  const currentEx = w?.exercises[exIdx];
  const nextEx = w?.exercises[exIdx + 1];

  const completeSet = () => {
    if (!currentEx) return;
    const key = exIdx;
    const done = (completedSets[key] || 0) + 1;
    setCompletedSets({ ...completedSets, [key]: done });

    if (done >= currentEx.sets) {
      // move to next exercise
      if (exIdx + 1 >= w.exercises.length) {
        // done!
        if (soundOn) announceSessionComplete();
        finish();
        return;
      }
      const nextExName = w.exercises[exIdx + 1].name;
      if (soundOn) announceNextExercise(nextExName);
      setExIdx(exIdx + 1);
      setSetIdx(0);
      setResting(true);
      setRestLeft(currentEx.rest_seconds);
    } else {
      if (soundOn) cues.setComplete();
      setSetIdx(done);
      setResting(true);
      setRestLeft(currentEx.rest_seconds);
    }
  };

  const skipRest = () => {
    setResting(false);
    setRestLeft(0);
    clearInterval(restTimerRef.current);
  };

  const finish = async (partial = false) => {
    const calories = Math.round((elapsed / 60) * 8); // rough estimate
    try {
      await api.post("/sessions", {
        workout_id: w.id,
        workout_title: w.title,
        duration_seconds: elapsed,
        exercises: w.exercises.map((ex, i) => {
          const done = completedSets[i] || 0;
          const wKg = weights[ex.exercise_id];
          return {
            exercise_id: ex.exercise_id,
            name: ex.name,
            sets_completed: done,
            reps_per_set: [],
            weight_kg: wKg ? Number(wKg) : null,
            all_completed: done >= ex.sets,
          };
        }),
        calories_estimate: calories,
      });
      toast.success(partial ? "Sessione salvata (parziale)" : "Sessione completata! Ottimo lavoro.");
      navigate("/progress");
    } catch {
      toast.error("Errore salvataggio sessione");
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const progress = useMemo(() => {
    if (!w) return 0;
    const totalSets = w.exercises.reduce((a, e) => a + e.sets, 0);
    const done = Object.values(completedSets).reduce((a, b) => a + b, 0);
    return Math.round((done / totalSets) * 100);
  }, [w, completedSets]);

  if (!w) return <div className="p-8 text-[#A0A0AB]">Caricamento...</div>;

  return (
    <div className="min-h-screen bg-[#0B0B0F]">
      {/* header */}
      <div className="border-b border-[#2A2A35] px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to={`/workouts/${w.id}`} className="text-xs uppercase tracking-widest text-[#A0A0AB] hover:text-white flex items-center gap-1" data-testid="back-workout">
          <ArrowLeft className="w-3 h-3" /> Esci
        </Link>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB]">Durata</div>
            <div className="font-mono-alt text-lg font-bold" data-testid="session-elapsed">{fmt(elapsed)}</div>
          </div>
          <button
            onClick={toggleSound}
            data-testid="session-sound-toggle"
            className={`border p-2 transition-all ${
              soundOn ? "border-[#E2FF31] text-[#E2FF31]" : "border-[#2A2A35] text-[#A0A0AB]"
            }`}
            aria-label={soundOn ? "Disattiva audio" : "Attiva audio"}
            title={soundOn ? "Audio attivo" : "Audio disattivato"}
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setPaused((p) => !p)}
            data-testid="session-pause"
            className="border border-[#2A2A35] hover:border-white p-2"
          >
            {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <button
            onClick={() => window.confirm("Terminare l'allenamento e salvare come parziale?") && finish(true)}
            data-testid="session-quit"
            className="border border-[#2A2A35] hover:border-[#EF4444] hover:text-[#EF4444] p-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* progress bar */}
      <div className="h-1 bg-[#141419]">
        <div className="h-full bg-[#FF3B30] transition-all" style={{ width: `${progress}%` }} data-testid="session-progress" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {resting ? (
          <div className="text-center py-12" data-testid="rest-screen">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E2FF31] mb-4">RECUPERO</div>
            <div className="font-display text-[10rem] sm:text-[14rem] font-black text-[#E2FF31] leading-none tracking-tighter" data-testid="rest-timer">
              {restLeft}
            </div>
            <div className="text-[#A0A0AB] mb-6">Respira. Prossimo: <span className="text-white font-bold uppercase">{w.exercises[exIdx].name}</span> · Serie {setIdx + 1}/{w.exercises[exIdx].sets}</div>
            <div className="flex items-center gap-3 justify-center">
              <button
                onClick={skipRest}
                data-testid="skip-rest"
                className="border border-[#2A2A35] hover:border-white text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2"
              >
                <SkipForward className="w-4 h-4" /> Salta recupero
              </button>
              <button
                onClick={() => setVideoEx(enrichEx(w.exercises[exIdx]))}
                data-testid="rest-video"
                className="border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2"
              >
                <PlayCircle className="w-4 h-4" /> Ripassa video
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-2">
              Esercizio {exIdx + 1} / {w.exercises.length}
            </div>
            <div className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tighter leading-[0.9] mb-4" data-testid="current-ex-name">
              {currentEx.name}
            </div>
            <button
              onClick={() => setVideoEx(enrichEx(currentEx))}
              data-testid="current-ex-video"
              className="mb-6 border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] text-xs font-bold uppercase tracking-widest px-4 py-2 inline-flex items-center gap-2 transition-all"
            >
              <PlayCircle className="w-4 h-4" strokeWidth={2.5} /> Video demo
            </button>

            {/* Progressione */}
            {(() => {
              const prog = progressions[currentEx.exercise_id];
              if (!prog) return null;
              const eq = prog.exercise?.equipment;
              const showWeight = eq === "dumbbells" || eq === "kettlebell";
              return (
                <div className="border border-[#2A2A35] p-4 mb-6 bg-[#141419]/40" data-testid="progression-box">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB] mb-1">Ultima volta</div>
                      {prog.last ? (
                        <div className="font-mono-alt text-sm">
                          {prog.last.weight_kg ? <span className="text-white">{prog.last.weight_kg}kg × </span> : null}
                          <span className="text-white">{prog.last.sets_completed} serie</span>
                          {prog.last.all_completed && <span className="text-[#22C55E] ml-2">✓ complete</span>}
                          <div className="text-[10px] text-[#A0A0AB] mt-0.5">{new Date(prog.last.date).toLocaleDateString("it-IT")}</div>
                        </div>
                      ) : (
                        <div className="text-sm text-[#A0A0AB]">Prima volta con questo esercizio</div>
                      )}
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-[#E2FF31] mb-1">Suggerito oggi</div>
                      <div className="font-mono-alt text-sm text-white">
                        {showWeight && prog.suggested?.weight_kg != null && (
                          <span>{prog.suggested.weight_kg}kg × </span>
                        )}
                        <span>{prog.suggested?.reps ?? currentEx.reps} rip</span>
                      </div>
                      {prog.suggested?.note && (
                        <div className="text-[10px] text-[#A0A0AB] mt-1 leading-tight">{prog.suggested.note}</div>
                      )}
                    </div>
                  </div>
                  {showWeight && (
                    <div className="mt-3 pt-3 border-t border-[#2A2A35] flex items-center gap-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">Peso usato oggi</div>
                      <input
                        type="number"
                        step="0.5"
                        value={weights[currentEx.exercise_id] || ""}
                        onChange={(e) => setWeights({ ...weights, [currentEx.exercise_id]: e.target.value })}
                        data-testid="weight-input"
                        className="bg-[#0B0B0F] border border-[#2A2A35] focus:border-[#FF3B30] focus:outline-none px-3 py-1.5 w-24 font-mono-alt text-sm"
                      />
                      <span className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">kg</span>
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="border border-[#2A2A35] p-4">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB] mb-1">Serie</div>
                <div className="font-display text-4xl font-black" data-testid="set-counter">
                  {(completedSets[exIdx] || 0) + (setIdx === currentEx.sets ? 0 : 1)}<span className="text-[#A0A0AB]">/{currentEx.sets}</span>
                </div>
              </div>
              <div className="border border-[#2A2A35] p-4">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB] mb-1">Ripetizioni</div>
                <div className="font-display text-4xl font-black text-[#FF3B30]">{currentEx.reps}</div>
              </div>
              <div className="border border-[#2A2A35] p-4">
                <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB] mb-1">Recupero</div>
                <div className="font-display text-4xl font-black">{currentEx.rest_seconds}s</div>
              </div>
            </div>

            <button
              onClick={completeSet}
              data-testid="complete-set-btn"
              className="w-full bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-lg py-6 hover:bg-[#e02e24] transition-all flex items-center justify-center gap-3"
            >
              <Check className="w-6 h-6" strokeWidth={3} />
              Serie completata
            </button>

            {nextEx && (
              <div className="mt-8 border-l-2 border-[#E2FF31] pl-4">
                <div className="text-[9px] uppercase tracking-widest text-[#E2FF31]">Prossimo</div>
                <div className="font-display text-lg font-bold uppercase" data-testid="next-ex-name">{nextEx.name}</div>
              </div>
            )}
          </div>
        )}
      </div>
      <VideoDialog exercise={videoEx} onClose={() => setVideoEx(null)} />
    </div>
  );
}
